// file: src/components/layout/LeftSidebar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  ChevronDown, 
  ChevronRight, 
  FilePlus, 
  Info, 
  Trash2, 
  FileText, 
  Lightbulb,
  Settings,
  ClipboardList,
  Layers,
  Link,
  Zap,
  Package,
  Upload,
  Edit,
  Settings2,
  CircuitBoard,
  FileCode,
  Cpu,
  Wrench,
  TestTube,
  LifeBuoy,
  Factory,
  DraftingCompass,
  GitMerge,
  Unlink,
  Server,
  SquareArrowUpRight,
  HardDrive
} from 'lucide-react';

import { useProjectContext } from '../../context/ProjectContext';
import { useTheme } from '../../context/ThemeContext';
import { useProjectsData } from '../../hooks/useProjectsData';
import { useViewerContext } from '../../context/ViewerContext';

// Import CSS module styles
import styles from './LeftSidebar.module.css';

// Import the proper file icons and components
import FileTypeIcon from '../icons/FileTypeIcon';
import FileStatusIcon from '../icons/FileStatusIcon';
import { detectFileType } from '../../utils/fileUtils';

// Import PhaseInfoModal
import PhaseInfoModal from '../modals/PhaseInfoModal';
import FileDeleteConfirmModal from '../modals/FileDeleteConfirmModal';
import StorageContentsModal from '../modals/StorageContentsModal';
import ProtectedAction from '../common/ProtectedAction';

// Phase icons and colors mapping based on the System V Diagram template card colors
const PHASE_ICONS = {
  'concept': { icon: Lightbulb, color: "text-red-500" },
  'conceptexploration': { icon: Lightbulb, color: "text-red-500" },
  'architecture': { icon: Layers, color: "text-yellow-500" },
  'requirements': { icon: ClipboardList, color: "text-orange-500" },
  'detaileddesign': { icon: DraftingCompass, color: "text-green-500" },
  'detailed': { icon: DraftingCompass, color: "text-green-500" },
  'design': { icon: DraftingCompass, color: "text-green-500" },
  'integration': { icon: GitMerge, color: "text-cyan-500" },
  'integrationtesting': { icon: GitMerge, color: "text-cyan-500" },
  'testing': { icon: GitMerge, color: "text-cyan-500" },
  'manufacturing': { icon: Factory, color: "text-blue-500" },
  'operations': { icon: Settings, color: "text-purple-500" },
  'operationsmaintenance': { icon: Settings, color: "text-purple-500" },
  'maintenance': { icon: Settings, color: "text-purple-500" }
};

// Helper function to get phase configuration
function getPhaseConfig(phaseName) {
  const cleanName = phaseName.toLowerCase().replace(/[^a-z]/g, '');
  
  // Try exact match first
  if (PHASE_ICONS[cleanName]) {
    return PHASE_ICONS[cleanName];
  }
  
  // Try partial matches for compound names
  for (const key in PHASE_ICONS) {
    if (cleanName.includes(key) || key.includes(cleanName)) {
      return PHASE_ICONS[key];
    }
  }
  
  // Default fallback
  return { icon: FileText, color: "text-slate-400" };
}

// Custom hook for sidebar resizing
function useSidebarResize(initialWidth = 320) {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      // Min width should accommodate "Operations & Maintenance" + icons + padding
      const minWidth = 280;
      const maxWidth = 500;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  };

  return { width, handleMouseDown, sidebarRef };
}

export default function LeftSidebar({
  onFileOpen,
  onFileRename,
  onFileDelete,
  onPhaseUpload,
  onProjectDelete,
  onRequestReparse,
  className = '',
  style = {}
}) {
  const { theme } = useTheme();
  const { width, handleMouseDown, sidebarRef } = useSidebarResize(320);
  
  // Get projects and actions from context
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
    deleteProject,
    renameProject,
    runDocumentsInProject,
    getActiveProject,
  } = useProjectContext();

  // Get the active project
  const activeProject = getActiveProject();
  const [searchParams] = useSearchParams();

  // ----------------------------------------------------------------
  // Expand/Collapse state
  const [expandedPhases, setExpandedPhases] = useState({});

  // Update expanded states when active project changes
  useEffect(() => {
    if (activeProject) {
      const newExpandedPhases = { ...expandedPhases };
      let changed = false;
      
      activeProject.phases.forEach(ph => {
        if (newExpandedPhases[ph.id] == null) {
          newExpandedPhases[ph.id] = true;
          changed = true;
        }
      });

      if (changed) {
        setExpandedPhases(newExpandedPhases);
      }
    }
  }, [activeProject]);

  // ----------------------------------------------------------------
  // Delete and rename states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showFileDeleteConfirm, setShowFileDeleteConfirm] = useState(null);
  const [showDisassociateConfirm, setShowDisassociateConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDisassociating, setIsDisassociating] = useState(false);
  const [renamingDocId, setRenamingDocId] = useState(null);
  const [tempDocName, setTempDocName] = useState('');
  const [renamingProjectId, setRenamingProjectId] = useState(null);
  const [tempProjectName, setTempProjectName] = useState('');

  // ----------------------------------------------------------------
  // Modal states
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [showStorageModal, setShowStorageModal] = useState(false);

  // ----------------------------------------------------------------
  // Handlers
  function handlePhaseExpand(phaseId) {
    setExpandedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  }

  function handlePhaseInfo(phaseName) {
    setSelectedPhase(phaseName);
  }

  function handleDeleteClick(e, projectId) {
    e.stopPropagation();
    setShowDeleteConfirm(projectId);
  }

  function confirmDeleteProject(e, projectId) {
    e.stopPropagation();
    if (onProjectDelete) {
      onProjectDelete(projectId);
    }
    setShowDeleteConfirm(null);
  }

  function cancelDeleteProject(e) {
    e.stopPropagation();
    setShowDeleteConfirm(null);
  }

  function handleUploadClick(e, projectId, phaseId) {
    e.stopPropagation();
    if (onPhaseUpload) {
      onPhaseUpload(projectId, phaseId);
    }
  }

  function handleFileClick(file) {
    if (onFileOpen) {
      onFileOpen(file);
    }
  }

  // File deletion handler
  function handleFileDelete(e, file) {
    e.stopPropagation();
    setShowFileDeleteConfirm(file);
  }

  // JupyterLab server disassociation handler
  function handleServerDisassociate(e, file) {
    e.stopPropagation();
    setShowDisassociateConfirm(file);
  }

  async function confirmDeleteFile() {
    if (!showFileDeleteConfirm) return;
    
    setIsDeleting(true);
    try {
      if (onFileDelete) {
        await onFileDelete(showFileDeleteConfirm);
      }
      setShowFileDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting file:', error);
      // Keep modal open on error so user can try again or cancel
    } finally {
      setIsDeleting(false);
    }
  }

  async function confirmDisassociateServer() {
    if (!showDisassociateConfirm) return;
    
    setIsDisassociating(true);
    try {
      // For JupyterLab files, disassociate is the same as delete - both remove the virtual document
      // So we can just use the existing file deletion logic
      if (onFileDelete) {
        await onFileDelete(showDisassociateConfirm);
      }
      
      setShowDisassociateConfirm(null);
    } catch (error) {
      console.error('Error disassociating JupyterLab server:', error);
      // Keep modal open on error so user can try again or cancel
    } finally {
      setIsDisassociating(false);
    }
  }

  function cancelDeleteFile() {
    setShowFileDeleteConfirm(null);
    setIsDeleting(false);
  }

  function cancelDisassociateServer() {
    setShowDisassociateConfirm(null);
    setIsDisassociating(false);
  }

  // File reparse handler
  function handleFileReparse(file) {
    if (onRequestReparse) {
      onRequestReparse(file);
    }
  }

  // Show loading state if projects are still loading
  if (projectsLoading) {
    return (
      <div 
        ref={sidebarRef}
        className={`${styles.leftSidebar} glass-effect ${className} ${
          theme === 'dark' 
            ? 'bg-slate-900/90 border-slate-700 text-slate-200' 
            : 'bg-slate-50/90 border-slate-300 text-slate-800'
        }`}
        style={{ ...style, width: `${width}px` }}
      >
        {/* Drag handle for resizing */}
        <div
          onMouseDown={handleMouseDown}
          className={styles.sidebarDragHandle}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(59, 130, 246, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
          }}
        />
        
        <div className="flex flex-col items-center justify-center h-32 p-4" style={{ position: 'relative', zIndex: 10 }}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading project...</p>
        </div>
      </div>
    );
  }
  
  // Show error state if projects failed to load
  if (projectsError) {
    return (
      <div 
        ref={sidebarRef}
        className={`${styles.leftSidebar} glass-effect ${className} ${
          theme === 'dark' 
            ? 'bg-slate-900/90 border-slate-700 text-slate-200' 
            : 'bg-slate-50/90 border-slate-300 text-slate-800'
        }`}
        style={{ ...style, width: `${width}px` }}
      >
        {/* Drag handle for resizing */}
        <div
          onMouseDown={handleMouseDown}
          className={styles.sidebarDragHandle}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(59, 130, 246, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
          }}
        />
        
        <div className="flex flex-col items-center justify-center h-32 p-4" style={{ position: 'relative', zIndex: 10 }}>
          <p className="text-sm text-red-400">Failed to load projects</p>
          <p className="text-xs text-muted-foreground mt-1">{projectsError.message}</p>
        </div>
      </div>
    );
  }

  // If no active project, show a message
  if (!activeProject) {
    return (
      <div 
        ref={sidebarRef}
        className={`relative overflow-y-auto glass-effect ${
          theme === 'dark' 
            ? 'bg-slate-900/90 border-slate-700 text-slate-200' 
            : 'bg-slate-50/90 border-slate-300 text-slate-800'
        }`}
        style={{ 
          width: `${width}px`,
          height: 'calc(100vh - 68px)',
          marginTop: '68px',
          marginBottom: '8px',
          borderTopLeftRadius: '8px',
          borderBottomLeftRadius: '8px'
        }}
      >
        {/* Drag handle for resizing */}
        <div
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            right: -2,
            top: 0,
            width: '4px',
            height: '100%',
            cursor: 'col-resize',
            background: 'transparent',
            zIndex: 10,
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(59, 130, 246, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
          }}
        />
        
        <div className="p-4">
          <div className="flex flex-col items-center justify-center h-32">
            <Package className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground text-center">No active project selected</p>
            <p className="text-xs text-muted-foreground text-center mt-1">Create a new project or select one from chat history</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        ref={sidebarRef}
        className={`${styles.leftSidebar} glass-effect ${className} ${
          theme === 'dark' 
            ? 'bg-slate-900/90 border-slate-700 text-slate-200' 
            : 'bg-slate-50/90 border-slate-300 text-slate-800'
        }`}
        style={{ ...style, width: `${width}px` }}
      >
        {/* Drag handle for resizing - only show on tablet+ */}
        <div className={styles.sidebarDragHandle} onMouseDown={handleMouseDown} />

        <div className="p-4 h-full">
          {/* Project header */}
          <div className="mb-4">
            <h2 className={`text-sm font-semibold uppercase tracking-wider mb-2 ${
              theme === 'dark' ? 'text-slate-500' : 'text-slate-500'
            }`}>
              Current Project
            </h2>

            <div className={`px-3 py-2 rounded-md ${
              theme === 'dark' 
                ? 'bg-cyan-900/30 text-cyan-200 border border-cyan-800/50'
                : 'bg-blue-100 text-blue-900 border border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
            {renamingProjectId === activeProject.id ? (
              <input
                    className={`bg-transparent border-none outline-none text-sm flex-1 ${
                      theme === 'dark' ? 'text-cyan-200' : 'text-blue-900'
                    }`}
                value={tempProjectName}
                onChange={e => setTempProjectName(e.target.value)}
                    onBlur={() => {
                      // Handle project rename
                      setRenamingProjectId(null);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        // Handle project rename
                        setRenamingProjectId(null);
                      } else if (e.key === 'Escape') {
                        setRenamingProjectId(null);
                      }
                    }}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span
                    className="truncate cursor-pointer"
                onDoubleClick={(e) => {
                  e.stopPropagation();
                      setRenamingProjectId(activeProject.id);
                      setTempProjectName(activeProject.name);
                }}
              >
                {activeProject.name}
              </span>
            )}

            <button
                  onClick={(e) => handleDeleteClick(e, activeProject.id)}
                  className={`p-1 rounded-md transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-red-900/30 text-slate-400 hover:text-red-300'
                      : 'hover:bg-red-100 text-slate-500 hover:text-red-600'
                  }`}
                  aria-label="Delete project"
                >
                  <Trash2 size={16} />
            </button>
          </div>

              {/* Delete confirmation */}
              {showDeleteConfirm === activeProject.id && (
                <div className={`mt-2 p-2 rounded ${
                  theme === 'dark' ? 'bg-slate-800' : 'bg-slate-200'
                }`}>
                  <p className="text-sm mb-2">Delete this project?</p>
                  <div className="flex space-x-2">
                    <button
                      onClick={(e) => confirmDeleteProject(e, activeProject.id)}
                      className={`text-xs px-2 py-1 rounded ${
                        theme === 'dark'
                          ? 'bg-red-900/40 hover:bg-red-900/60 text-red-300'
                          : 'bg-red-100 hover:bg-red-200 text-red-800'
                      }`}
                    >
                      Delete
                    </button>
                    <button
                      onClick={cancelDeleteProject}
                      className={`text-xs px-2 py-1 rounded ${
                        theme === 'dark'
                          ? 'bg-slate-700 hover:bg-slate-600'
                          : 'bg-slate-300 hover:bg-slate-400'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Storage section */}
          <div className="mb-4">
            <h2 className={`text-sm font-semibold uppercase tracking-wider mb-2 ${
              theme === 'dark' ? 'text-slate-500' : 'text-slate-500'
            }`}>
              Storage
            </h2>

            <ProtectedAction feature="start_server">
            <div
              onClick={() => setShowStorageModal(true)}
              className={`group flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-all duration-200 min-h-[40px] ${
                theme === 'dark' 
                  ? 'bg-cyan-900/30 hover:bg-cyan-800/40 text-cyan-200 border border-cyan-800/50 hover:border-cyan-700/70 hover:shadow-[0_0_15px_rgba(34,211,238,0.3)]'
                  : 'bg-blue-100 hover:bg-blue-200 text-blue-900 border border-blue-200 hover:border-blue-300 hover:shadow-[0_0_15px_rgba(59,130,246,0.3)]'
              }`}
            >
              <div className="flex items-center gap-2">
                <HardDrive className={`${
                  theme === 'dark' ? 'text-cyan-400' : 'text-blue-600'
                }`} size={16} />
                <span className="truncate font-medium">Manage Server Storage</span>
              </div>
              <SquareArrowUpRight className={`flex-shrink-0 ${
                theme === 'dark' ? 'text-cyan-400' : 'text-blue-600'
              }`} size={14} />
            </div>
            </ProtectedAction>
          </div>

          {/* Phases section */}
          <div>
            <h2 className={`text-sm font-semibold uppercase tracking-wider mb-2 ${
              theme === 'dark' ? 'text-slate-500' : 'text-slate-500'
            }`}>
              Phases
            </h2>

            <div className="space-y-1">
            {activeProject.phases.map(phase => {
              const isPhaseExpanded = expandedPhases[phase.id] ?? false;
                
                // Map phase name to icon configuration
                const phaseConfig = getPhaseConfig(phase.name);
                const PhaseIcon = phaseConfig.icon;
                const colorClass = phaseConfig.color;
                
                // Extract color name for hover glow effect
                let hoverGlowClass = '';
                if (colorClass.includes('red')) hoverGlowClass = 'hover-glow-red';
                else if (colorClass.includes('orange')) hoverGlowClass = 'hover-glow-orange';
                else if (colorClass.includes('yellow')) hoverGlowClass = 'hover-glow-yellow';
                else if (colorClass.includes('green')) hoverGlowClass = 'hover-glow-green';
                else if (colorClass.includes('cyan')) hoverGlowClass = 'hover-glow-cyan';
                else if (colorClass.includes('blue')) hoverGlowClass = 'hover-glow-blue';
                else if (colorClass.includes('purple')) hoverGlowClass = 'hover-glow-purple';
                
              return (
                  <div key={phase.id} className="mb-2">
                    {/* Phase header */}
                    <div 
                      className={`group flex items-center justify-between px-3 py-2 cursor-pointer transition-all duration-200 ${
                        theme === 'dark'
                          ? 'hover:bg-slate-800/50'
                          : 'hover:bg-slate-200/70'
                      } ${hoverGlowClass}`}
                      onClick={() => handlePhaseExpand(phase.id)}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="w-5 h-5 flex items-center justify-center">
                          {isPhaseExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          </span>
                        
                        <PhaseIcon className={`${colorClass} ${
                          theme === 'dark' 
                            ? colorClass 
                            : colorClass.replace('cyan', 'blue')
                        }`} size={18} />
                        
                        <span className="truncate">{phase.name}</span>
                      </div>
                      
                      <div className="flex transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                            handlePhaseInfo(phase.name);
                          }}
                          className={`p-1 transition-colors ${
                            theme === 'dark'
                              ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                              : 'hover:bg-slate-300 text-slate-500 hover:text-slate-900'
                          }`}
                          aria-label="Phase information"
                        >
                          <Info size={14} />
                        </button>
                        
                        <ProtectedAction feature="upload_file">
                        <button
                          onClick={(e) => handleUploadClick(e, activeProject.id, phase.id)}
                          className={`p-1 transition-colors ${
                            theme === 'dark'
                              ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                              : 'hover:bg-slate-300 text-slate-500 hover:text-slate-900'
                          }`}
                          aria-label="Upload document"
                        >
                          <FilePlus size={14} />
                      </button>
                        </ProtectedAction>
                      </div>
                    </div>
                    
                    {/* Phase files */}
                    {isPhaseExpanded && phase.files && phase.files.length > 0 && (
                      <div className="mt-1 ml-8 space-y-1">
                        {phase.files.map(file => (
                          <div key={file.id}>
                            <div 
                              className={`group flex items-center space-x-2 px-3 py-1.5 cursor-pointer transition-colors ${
                                theme === 'dark' 
                                  ? 'hover:bg-slate-800/75'
                                  : 'hover:bg-slate-200/60'
                              }`}
                              onClick={() => handleFileClick(file)}
                            >
                              {/* File type icon */}
                              <div className="flex-shrink-0">
                                <FileTypeIcon type={detectFileType(file.name)} />
                              </div>
                              
                              {/* File name */}
                              {renamingDocId === file.id ? (
                                  <input
                                  className={`bg-transparent border-none outline-none text-sm flex-1 ${
                                    theme === 'dark' ? 'text-slate-200' : 'text-slate-800'
                                  }`}
                                    value={tempDocName}
                                    onChange={e => setTempDocName(e.target.value)}
                                  onBlur={() => {
                                    // Handle doc rename
                                    setRenamingDocId(null);
                                  }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      // Handle doc rename
                                      setRenamingDocId(null);
                                    } else if (e.key === 'Escape') {
                                      setRenamingDocId(null);
                                    }
                                  }}
                                    autoFocus
                                  />
                                ) : (
                                <span 
                                  className="truncate text-sm font-medium flex-1"
                                  onDoubleClick={(e) => {
                                    e.stopPropagation();
                                    setRenamingDocId(file.id);
                                    setTempDocName(file.name);
                                  }}
                                >
                                  {file.name}
                                </span>
                              )}
                              
                              {/* File status and actions */}
                              <div className="flex items-center space-x-1 flex-shrink-0">
                                {/* AI parsing status */}
                                <FileStatusIcon 
                                  run={file.run}
                                  progress={file.progress}
                                  progress_msg={file.progress_msg}
                                  onRetry={() => runDocumentsInProject(file.id)}
                                  onReparse={() => handleFileReparse(file)}
                                  docName={file.name}
                                  type={file.type}
                                />
                                
                                {/* Action button - Disassociate for Blender/FreeCAD/JupyterLab/KiCad, Delete for regular files */}
                                {file.type === 'blender' || file.type === 'freecad' || file.type === 'jupyterlab' || file.type === 'kicad' ? (
                                  <button
                                    onClick={(e) => handleServerDisassociate(e, file)}
                                    className={`p-1 transition-colors ${
                                      theme === 'dark'
                                        ? 'hover:bg-orange-900/30 text-slate-400 hover:text-orange-300'
                                        : 'hover:bg-orange-100 text-slate-500 hover:text-orange-600'
                                    }`}
                                    aria-label={`Disassociate ${file.type === 'blender' ? 'Blender' : file.type === 'freecad' ? 'FreeCAD' : file.type === 'jupyterlab' ? 'JupyterLab' : 'KiCad'} server`}
                                    title="Disassociate from knowledgebase"
                                  >
                                    <Unlink size={14} />
                                  </button>
                                ) : (
                                <button
                                  onClick={(e) => handleFileDelete(e, file)}
                                  className={`p-1 transition-colors ${
                                    theme === 'dark'
                                      ? 'hover:bg-red-900/30 text-slate-400 hover:text-red-300'
                                      : 'hover:bg-red-100 text-slate-500 hover:text-red-600'
                                  }`}
                                  aria-label="Delete document"
                                >
                                  <Trash2 size={14} />
                                </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        </div>
      </div>

      {/* Phase Info Modal */}
      {selectedPhase && (
        <PhaseInfoModal
          phaseName={selectedPhase}
          onClose={() => setSelectedPhase(null)}
        />
      )}

      {/* Storage Modal */}
      {showStorageModal && (
        <StorageContentsModal
          onClose={() => setShowStorageModal(false)}
        />
      )}

      {/* File Delete Confirmation Modal */}
      {showFileDeleteConfirm && (
        <FileDeleteConfirmModal
          fileName={showFileDeleteConfirm.name}
          onClose={cancelDeleteFile}
          onConfirm={confirmDeleteFile}
          isDeleting={isDeleting}
        />
      )}

      {/* JupyterLab Server Disassociate Confirmation Modal */}
      {showDisassociateConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', padding: '1rem' }}>
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-card/95 shadow-xl border border-gray-400 dark:border-border/50">
            <div className="mb-4">
              <div className="flex items-center mb-3">
                <div className="size-8 rounded-full bg-orange-500/10 text-orange-500 flex items-center justify-center mr-3">
                  <Unlink className="h-4 w-4" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">Disassociate Server</h2>
              </div>
              <p className="text-sm text-gray-700 dark:text-muted-foreground">
                Are you sure you want to disassociate "<strong className="text-foreground">{showDisassociateConfirm.name}</strong>" from this knowledgebase? 
                This will remove the server from the file list but won't delete the server itself.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDisassociateServer}
                disabled={isDisassociating}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDisassociateServer}
                disabled={isDisassociating}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
                  isDisassociating
                    ? 'bg-orange-400 text-white cursor-not-allowed'
                    : theme === 'dark'
                    ? 'bg-orange-600 hover:bg-orange-700 text-white'
                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                }`}
              >
                {isDisassociating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Disassociating...
                  </>
                ) : (
                  'Disassociate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}